import { useCallback } from 'react';
import {
  LocationFeatureFlagSettingName,
  LocationFeatureSettings,
} from '@weave/schema-gen-ts/dist/schemas/payments/messages/location_settings/location_settings.pb';
import { useQueryClient } from 'react-query';
import { PaymentsView1, useUpdateMultiLocationFeatureSettingsCache } from '@frontend/api-payments-view';
import { useTranslation } from '@frontend/i18n';
import { useMutation } from '@frontend/react-query-helpers';
import { SchemaPaymentsViewsService } from '@frontend/schema';
import { useFormField, useModalControl, useAlert } from '@frontend/design-system';
import { useSelectedLocations } from '../../providers/selected-location-context';
import { getLocationFeatureSettingsQueryKey } from '../locations/use-location-feature-settings';
import { SURCHARGE_MIN_VALUE, useLocationCanSurcharge } from './use-location-can-surcharge';
import { useLocationSurchargeState } from './use-location-surcharge-state';

interface UseToggleLocationSurchargeParams {
  locationIds?: string[];
}

export const useToggleLocationSurcharge = ({
  locationIds: locationIdsParam,
}: UseToggleLocationSurchargeParams = {}) => {
  const { t } = useTranslation('payments');
  const alert = useAlert();

  const { locationIds: locationIdsFromUtils } = useSelectedLocations();
  const locationIds = (locationIdsParam ?? locationIdsFromUtils).filter(Boolean) as string[];
  const queryClient = useQueryClient();

  const { enabledLocations: canSurchargeLocations, combinedMaxSurchargeInfo } = useLocationCanSurcharge(locationIds);

  const {
    enabled,
    isAllEnabled,
    isLoading,
    firstPercentValue: percentValue,
    featureSettingEnabledLocations,
    refetchAll: refetchFeatureState,
  } = useLocationSurchargeState(locationIds);

  const { value: switchValue, ...switchProps } = useFormField(
    {
      value: canSurchargeLocations.length >= 1 && isAllEnabled === true,
      type: 'switch',
    },
    [isAllEnabled, canSurchargeLocations.length]
  );

  const { modalProps, triggerProps } = useModalControl();

  const updateMultiLocationFeatureSettingsCache = useUpdateMultiLocationFeatureSettingsCache();

  const { mutate: toggleFeatureFlag, isLoading: isTogglingFeatureFlag } = useMutation(
    async (value: boolean) => {
      const requests = canSurchargeLocations.map((locationId) => {
        return SchemaPaymentsViewsService.SetLocationFeatureEnabled({
          locationId,
          settingName: LocationFeatureFlagSettingName.SURCHARGING_ENABLED,
          enabled: value,
        }).then((res) => {
          if (locationId) {
            updateMultiLocationFeatureSettingsCache(
              locationId,
              LocationFeatureFlagSettingName.SURCHARGING_ENABLED,
              value
            );
          }
          return res;
        });
      });
      await Promise.all(requests);
    },
    {
      onError: () => {
        alert.error(t('Error changing surcharge enabled status. Please try again.'));
      },
      onSettled: () => {
        refetchFeatureState();
      },
    }
  );

  const { mutateAsync: mutatePercentValue, isLoading: isMutatingPercentValue } =
    PaymentsView1.Mutations.useSetLocationSurchargePercentageMutation();

  const handleFeatureFlagToggle = async () => {
    await toggleFeatureFlag(!switchValue);
    modalProps.onClose();
  };

  const handleOpenModal = useCallback(() => triggerProps.onClick(), [triggerProps]);

  const onChangePercentValue = useCallback(
    async (percent: number) => {
      const safePercent = Math.round(percent);

      if (safePercent < SURCHARGE_MIN_VALUE || safePercent > combinedMaxSurchargeInfo.maximumSurchargePercentage) {
        return;
      }

      try {
        /**
         * Make a mutation request for each location
         * Schema generated mutation handlers don't support multiple.
         * So don't pass in `onSuccess()` here.
         *
         * We'll do state updates below.
         */
        const requests = canSurchargeLocations.map(async (locationId) => {
          return mutatePercentValue({
            surchargePercentage: safePercent,
            locationId,
          });
        });

        await Promise.all(requests);

        /**
         * All the mutations succeeded.
         * Update state.
         */

        canSurchargeLocations.forEach((locationId) => {
          if (!locationId) return;

          const queryKey = getLocationFeatureSettingsQueryKey(
            locationId,
            LocationFeatureFlagSettingName.SURCHARGING_ENABLED
          );

          const prevValue = queryClient.getQueryData<{ settings: LocationFeatureSettings }>(queryKey);

          // update the single endpoint cache
          queryClient.setQueryData<{ settings: LocationFeatureSettings }>(queryKey, {
            ...prevValue,
            settings: {
              ...prevValue?.settings,
              surchargePercentage: safePercent,
            },
          });

          // update the multi endpoint cache
          updateMultiLocationFeatureSettingsCache(locationId, 'surchargePercentage', safePercent);
        });

        alert.success(t('Surcharge percentage updated successfully.'));
      } catch (error) {
        alert.error(t('Error updating surcharge percentage.'));
        console.error('Error mutating percent value', error);
      }
    },
    [mutatePercentValue, canSurchargeLocations]
  );

  return {
    featureSettingSwitchProps: {
      ...switchProps,
      value: switchValue,
      disabled: isTogglingFeatureFlag || isLoading,
      onChange: !switchValue ? handleOpenModal : handleFeatureFlagToggle,
      indeterminate: enabled === 'mixed',
    },
    onModalConfirm: handleFeatureFlagToggle,
    modalProps,
    percentValue,
    onChangePercentValue,
    isMutatingPercentValue,

    /**
     * Locations that ARE ALLOWED to surcharge.
     * They can turn the setting on or off.
     */
    surchargeEnabledLocations: canSurchargeLocations.filter(Boolean) as string[],

    /**
     * Surcharge enabled via feature setting
     * This turns the switch on/off
     */
    featureSettingEnabledLocations,

    locationIdsInPlay: locationIds,

    combinedMaxSurchargeInfo,
  };
};
